/**
 * 자동매매 조회 타입. (2026-09-20, BACKLOG I-5 A3)
 *
 * 백엔드 `AutoTradeConfigResponse` · `AutoTradeResponse` 와 1:1 대응한다.
 */

/**
 * 자동매매 모드 네 단계.
 *
 * 🔴 돈이 움직이는 경계는 SHADOW 와 SEMI_AUTO 사이다
 *   화면이 그 경계에서 경고 띠를 띄운다. 판정은 서버가 내려주는
 *   `placesRealOrders` 를 쓴다 — 여기서 다시 판단하면 언젠가 갈린다.
 */
export type AutoTradeMode = 'OFF' | 'SHADOW' | 'SEMI_AUTO' | 'SMALL_AUTO';

/** 주문 시도의 결말. 백엔드 AutoTradeDecision 과 같다. */
export type AutoTradeDecision =
  | 'SHADOW'
  | 'PENDING'
  | 'APPROVED'
  | 'SENT'
  | 'FILLED'
  | 'REJECTED'
  | 'FAILED'
  | 'EXPIRED';

export interface AutoTradeConfig {
  mode: AutoTradeMode;
  /** 🔴 서버가 판정한다. 화면에서 모드로 다시 계산하지 않는다 */
  placesRealOrders: boolean;
  sourceProfile: string;
  /** 따라가는 계좌의 화면 이름. 예) 저점매수(리스크) */
  sourceLabel: string;
  country: string;
  maxPerStockAmount: number;
  maxDailyOrders: number;
  maxDailyLossAmount: number;
  /** 전체 노출 한도(보유 원가 합). 🔴 null 이면 "제한 없음" 이 아니라 실주문 매수 거부다 (2026-09-27) */
  maxTotalExposureAmount: number | null;
  conditionalOrderEnabled: boolean;
  /** 예약된 모드. 있으면 pendingEffectiveDate 부터 적용된다 */
  pendingMode: AutoTradeMode | null;
  pendingEffectiveDate: string | null;
  lastChangeNote: string | null;
}

export interface AutoTradeOrder {
  id: number;
  mode: AutoTradeMode;
  country: string;
  symbol: string;
  side: 'BUY' | 'SELL';
  orderKind: string;
  quantity: number;
  price: number | null;
  signalSource: string;
  /** signalSource 의 화면 이름(서버 AutoTradeSource.labelOf — 예: 저점매수(리스크) · 하이리스크). 기록의 "계좌" 칸 */
  sourceLabel: string;
  /** 어느 규칙이 걸렸나. 한도 환산 내역도 여기 붙는다 */
  signalReason: string;
  decision: AutoTradeDecision;
  /** 거부·실패 사유. 어느 한도에 걸렸는지까지 담긴다 */
  decisionReason: string | null;
  requestedAt: string;
}

/** 모드를 사람이 읽는 말로. 화면 여러 곳에서 쓴다. */
export const MODE_LABEL: Record<AutoTradeMode, string> = {
  OFF: '꺼짐',
  SHADOW: '그림자',
  SEMI_AUTO: '반자동',
  SMALL_AUTO: '소액 자동',
};

/**
 * 모드가 무엇을 하는지 한 줄 설명.
 *
 * 🔴 "돈이 움직이나" 를 문구에 반드시 넣는다 (CLAUDE.md 6절 8항의 정신)
 *   주식 비전문가가 읽는다. "그림자" 라는 말만으로는 주문이 나가는지 알 수 없다.
 */
export const MODE_DESCRIPTION: Record<AutoTradeMode, string> = {
  OFF: '아무것도 하지 않습니다.',
  SHADOW: '낼 뻔한 주문을 기록만 합니다. 실제 주문은 나가지 않습니다.',
  SEMI_AUTO: '주문마다 승인을 묻습니다. 승인하면 실제로 주문이 나갑니다.',
  SMALL_AUTO: '한도 안에서 스스로 주문합니다. 실제 주문이 나갑니다.',
};

/** 결말을 사람이 읽는 말로. */
export const DECISION_LABEL: Record<AutoTradeDecision, string> = {
  SHADOW: '기록만',
  PENDING: '승인 대기',
  APPROVED: '승인됨',
  SENT: '주문 보냄',
  FILLED: '체결',
  /*
    ✏️ 2026-09-28 I-78 2차 — 전엔 "한도에 막힘" 이었다. 거절 사유에는 한도 말고도 중복 신호 · 설정 꺼짐 ·
      오늘 손익을 모름 · 보유 0주 매도가 있다(AutoTradeGate). 매도 거절에 "한도에 막힘" 이 붙으면
      같은 화면의 "파는 주문은 한도에 막히지 않습니다" 와 정면으로 어긋났다. 사유는 이유 칸이 말한다
  */
  REJECTED: '막힘',
  FAILED: '실패',
  EXPIRED: '시간 지남',
};

/**
 * 결말에 붙일 배지 톤.
 *
 * 🔴 REJECTED 를 실패로 칠하지 않는다
 *   그것은 고장이 아니라 **안전장치가 동작한 것**이다. 빨갛게 칠하면
 *   고쳐야 할 오류로 읽힌다. 실제로 고쳐야 하는 것은 FAILED 다.
 */
export function decisionTone(
  decision: AutoTradeDecision,
): 'neutral' | 'success' | 'warning' | 'danger' {
  switch (decision) {
    case 'FILLED':
    case 'APPROVED':
      return 'success';
    case 'FAILED':
      return 'danger';
    case 'REJECTED':
    case 'EXPIRED':
    case 'PENDING':
      return 'warning';
    default:
      return 'neutral';
  }
}
